import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { getPermissionLabel } from "../../../utils/permissions";
import "../Home/Homepage.css";
import "./SubAdminPage.css";

const DEFAULT_LIMIT = 20;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#?!@$%^&*-]).{8,}$/;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
};

const normalizeStatus = (status) =>
  String(status ?? "").toLowerCase() === "inactive" ? "Inactive" : "Active";

const SubAdminPage = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, pageLimit]);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await AdminService.getSubAdminPermissions();
      if (res?.success && Array.isArray(res?.data?.permissions)) {
        setPermissionCatalog(res.data.permissions);
      }
    } catch {
      setPermissionCatalog([]);
    }
  }, []);

  const fetchSubAdmins = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: pageLimit };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      const res = await AdminService.getSubAdmins(params);
      const list = Array.isArray(res?.data) ? res.data : [];
      setSubAdmins(list);
      setTotalPages(res?.pagination?.total_pages || 1);
      setTotalCount(
        typeof res?.pagination?.total === "number" ? res.pagination.total : null
      );
      if (res?.success === false && !list.length) {
        setError(res?.message || "Failed to load sub-admins.");
      }
    } catch (err) {
      setSubAdmins([]);
      setError(err?.response?.data?.message || err?.message || "Failed to load sub-admins.");
    } finally {
      setLoading(false);
    }
  }, [page, pageLimit, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    fetchSubAdmins();
  }, [fetchSubAdmins]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  const openCreate = () => {
    setSelectedAdmin(null);
    setModalMode("create");
  };

  const openEdit = (admin) => {
    setSelectedAdmin(admin);
    setModalMode("edit");
  };

  const openView = (admin) => {
    setSelectedAdmin(admin);
    setModalMode("view");
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedAdmin(null);
  };

  const handleStatusToggle = async (admin) => {
    const id = admin?.id;
    if (!id) return;
    const isActive = normalizeStatus(admin.status) === "Active";
    const nextStatus = isActive ? "inactive" : "active";
    setStatusUpdatingId(String(id));
    setMessage({ type: "", text: "" });
    try {
      const res = await AdminService.updateSubAdminStatus(String(id), nextStatus);
      if (res?.success) {
        showMessage("success", res?.message || "Status updated.");
        fetchSubAdmins();
      } else {
        showMessage("error", res?.message || "Failed to update status.");
      }
    } catch (err) {
      showMessage("error", err?.response?.data?.message || err?.message || "Failed to update status.");
    } finally {
      setStatusUpdatingId("");
    }
  };

  const getPermissionsDisplay = (admin) => {
    const keys = Array.isArray(admin?.permissions) ? admin.permissions : [];
    if (!keys.length) return "—";
    return keys
      .map((key) => getPermissionLabel(key, permissionCatalog))
      .join(", ");
  };

  const pageStart = subAdmins.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = subAdmins.length === 0 ? 0 : (page - 1) * pageLimit + subAdmins.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Sub Admin</span>
          </p>
          <h1 className="dashboard-hero__title">Sub Admin</h1>
          <p className="dashboard-hero__subtitle">
            Create sub-admins, assign module permissions, and manage access
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-user-shield" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="dashboard-activity-section">
          <div className="dashboard-activity-header dashboard-activity-header--with-action">
            <div className="dashboard-activity-header__left">
              <div className="dashboard-activity-header__icon">
                <i className="fas fa-users-cog" />
              </div>
              <h2 className="dashboard-activity-header__title">Sub Admin Management</h2>
            </div>
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <i className="fas fa-plus" />
              Create Sub Admin
            </button>
          </div>

          {error ? <p className="subadmin-page-message subadmin-page-message--error">{error}</p> : null}
          {message.text ? (
            <p
              className={`subadmin-page-message ${
                message.type === "error"
                  ? "subadmin-page-message--error"
                  : "subadmin-page-message--success"
              }`}
            >
              {message.text}
            </p>
          ) : null}

          <div className="subadmin-filters">
            <input
              type="search"
              className="subadmin-filters__input"
              placeholder="Search name, email, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="subadmin-filters__input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              className="subadmin-filters__input"
              value={pageLimit}
              onChange={(e) => setPageLimit(Number(e.target.value) || DEFAULT_LIMIT)}
            >
              {PAGE_LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>

          <div className="dashboard-content-inner">
            {loading ? (
              <p className="subadmin-page-empty">
                <i className="fas fa-spinner fa-spin" /> Loading sub-admins…
              </p>
            ) : subAdmins.length === 0 ? (
              <p className="subadmin-page-empty">No sub-admins found.</p>
            ) : (
              <>
                <div className="table-wrap subadmin-table-wrap">
                  <table className="data-table subadmin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Permissions</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subAdmins.map((row) => {
                        const isActive = normalizeStatus(row.status) === "Active";
                        const isUpdating = statusUpdatingId === String(row.id);
                        return (
                          <tr key={row.id}>
                            <td>{row.name ?? "—"}</td>
                            <td>{row.email ?? "—"}</td>
                            <td>{row.mobile_number ?? "—"}</td>
                            <td className="subadmin-table__roles">{getPermissionsDisplay(row)}</td>
                            <td>
                              <span
                                className={`badge badge--${isActive ? "active" : "inactive"}`}
                              >
                                {normalizeStatus(row.status)}
                              </span>
                            </td>
                            <td>{formatDate(row.created_at)}</td>
                            <td className="subadmin-table__actions">
                              <button
                                type="button"
                                className="btn-icon btn-icon--view"
                                onClick={() => openView(row)}
                                title="View"
                              >
                                <i className="fas fa-eye" />
                              </button>
                              <button
                                type="button"
                                className="btn-icon btn-icon--edit"
                                onClick={() => openEdit(row)}
                                title="Edit"
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                className={`btn-icon btn-icon--block ${
                                  isActive ? "" : "btn-icon--unblock"
                                }`}
                                onClick={() => handleStatusToggle(row)}
                                disabled={isUpdating}
                                title={isActive ? "Deactivate" : "Activate"}
                              >
                                <i
                                  className={`fas ${
                                    isUpdating
                                      ? "fa-spinner fa-spin"
                                      : isActive
                                        ? "fa-ban"
                                        : "fa-check-circle"
                                  }`}
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="subadmin-pagination">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span>
                    Showing {pageStart}–{pageEnd}
                    {totalCount != null ? ` of ${totalCount}` : ""}
                    {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {modalMode === "view" && selectedAdmin ? (
        <SubAdminViewModal
          admin={selectedAdmin}
          permissionCatalog={permissionCatalog}
          onClose={closeModal}
          onEdit={() => setModalMode("edit")}
        />
      ) : null}

      {modalMode === "create" || modalMode === "edit" ? (
        <SubAdminFormModal
          mode={modalMode}
          admin={selectedAdmin}
          permissionCatalog={permissionCatalog}
          saving={saving}
          onClose={closeModal}
          onSubmit={async (payload) => {
            setSaving(true);
            setMessage({ type: "", text: "" });
            try {
              const res =
                modalMode === "create"
                  ? await AdminService.createSubAdmin(payload)
                  : await AdminService.updateSubAdmin(String(selectedAdmin.id), payload);
              if (res?.success) {
                showMessage("success", res?.message || "Saved successfully.");
                closeModal();
                fetchSubAdmins();
              } else {
                showMessage("error", res?.message || "Save failed.");
              }
            } catch (err) {
              showMessage(
                "error",
                err?.response?.data?.message || err?.message || "Save failed."
              );
            } finally {
              setSaving(false);
            }
          }}
        />
      ) : null}
    </main>
  );
};

const SubAdminViewModal = ({ admin, permissionCatalog, onClose, onEdit }) => {
  const keys = Array.isArray(admin?.permissions) ? admin.permissions : [];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Sub Admin Details</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body">
          <dl className="detail-list">
            <div className="detail-list__row">
              <dt>Name</dt>
              <dd>{admin.name ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>Email</dt>
              <dd>{admin.email ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>Mobile</dt>
              <dd>{admin.mobile_number ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>Status</dt>
              <dd>
                <span
                  className={`badge badge--${
                    normalizeStatus(admin.status) === "Active" ? "active" : "inactive"
                  }`}
                >
                  {normalizeStatus(admin.status)}
                </span>
              </dd>
            </div>
            <div className="detail-list__row">
              <dt>Permissions</dt>
              <dd>
                {keys.length ? (
                  <div className="subadmin-permission-tags">
                    {keys.map((key) => (
                      <span key={key} className="subadmin-permission-tag">
                        {getPermissionLabel(key, permissionCatalog)}
                      </span>
                    ))}
                  </div>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div className="detail-list__row">
              <dt>Created</dt>
              <dd>{formatDate(admin.created_at)}</dd>
            </div>
            <div className="detail-list__row">
              <dt>Updated</dt>
              <dd>{formatDate(admin.updated_at)}</dd>
            </div>
          </dl>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn btn-primary" onClick={onEdit}>
              <i className="fas fa-edit" /> Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubAdminFormModal = ({ mode, admin, permissionCatalog, saving, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    name: admin?.name ?? "",
    email: admin?.email ?? "",
    mobile_number: admin?.mobile_number ?? "",
    password: "",
    confirmPassword: "",
    permissions: Array.isArray(admin?.permissions) ? [...admin.permissions] : [],
  });
  const [error, setError] = useState("");

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((k) => k !== key)
        : [...prev.permissions, key],
    }));
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Name must be at least 2 characters.");
      return;
    }
    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!/^\d{10,15}$/.test(String(form.mobile_number).trim())) {
      setError("Mobile number must be 10 to 15 digits.");
      return;
    }
    if (!form.permissions.length) {
      setError("Select at least one permission.");
      return;
    }
    if (mode === "create") {
      if (!form.password) {
        setError("Password is required.");
        return;
      }
      if (!PASSWORD_PATTERN.test(form.password)) {
        setError(
          "Password must be 8+ chars with upper, lower, number, and special (#?!@$%^&*-)."
        );
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    } else if (form.password) {
      if (!PASSWORD_PATTERN.test(form.password)) {
        setError(
          "Password must be 8+ chars with upper, lower, number, and special (#?!@$%^&*-)."
        );
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      mobile_number: String(form.mobile_number).trim(),
      permissions: form.permissions,
    };
    if (mode === "create" || form.password) {
      payload.password = form.password;
    }
    onSubmit(payload);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {mode === "create" ? "Create Sub Admin" : "Edit Sub Admin"}
          </h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          {error ? <div className="modal-error">{error}</div> : null}
          <div className="modal-field">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Full name"
              autoComplete="name"
            />
          </div>
          <div className="modal-field">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="admin@example.com"
              autoComplete="email"
            />
          </div>
          <div className="modal-field">
            <label>Mobile number</label>
            <input
              type="tel"
              value={form.mobile_number}
              onChange={(e) =>
                setForm((p) => ({ ...p, mobile_number: e.target.value.replace(/\D/g, "") }))
              }
              placeholder="10–15 digits"
              autoComplete="tel"
            />
          </div>
          <div className="modal-field">
            <label>Permissions</label>
            <div className="roles-checkbox-group">
              {permissionCatalog.length === 0 ? (
                <p className="subadmin-page-empty">Loading permissions…</p>
              ) : (
                permissionCatalog.map((item) => (
                  <label key={item.key} className="roles-checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(item.key)}
                      onChange={() => togglePermission(item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="modal-field">
            <label>{mode === "create" ? "Password" : "New password (optional)"}</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              placeholder="Min 8 chars, upper, lower, number, special"
              autoComplete="new-password"
            />
          </div>
          <div className="modal-field">
            <label>Confirm password</label>
            <input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving…
                </>
              ) : mode === "create" ? (
                "Create Sub Admin"
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubAdminPage;
